import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Link2, Loader2, RefreshCw, ShoppingBag, TriangleAlert } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { DESTRUCTIVE_OUTLINE, RelativeTime, StatusBadge, formatNumber } from '../../../components/design-system/admin';
import { PageContainer } from '../../../common/components/PageContainer';
import {
    CommerceGrant,
    ShopConnection,
    useCommerceApi,
    useShopConnection,
} from '../../../common/hooks/use-commerce-api.hook';
import { shopInsights } from '../../../modules/vendor/market/sales-insights';
import { Reading } from '../market/market-shared';

/**
 * The grants, in the order a vendor cares about them.
 *
 * Named for what they unlock, never for the platform's scope. A vendor
 * choosing `read_orders` is being asked to understand our data model; a vendor
 * choosing "Sales — revenue per product" is being asked whether they want the
 * feature.
 */
const GRANTS: ReadonlyArray<{ key: CommerceGrant; required?: boolean }> = [
    { key: 'sales' },
    { key: 'stock' },
    { key: 'catalog', required: true },
    { key: 'customers' },
    // Content packs add approved pictures to the shop's product pages (feat/room-to-revenue).
    { key: 'publish' },
];

const PLATFORM_LABEL: Record<string, string> = {
    shopify: 'Shopify',
    woocommerce: 'WooCommerce',
    prestashop: 'PrestaShop',
    magento: 'Magento',
    bigcommerce: 'BigCommerce',
    wix: 'Wix',
    squarespace: 'Squarespace',
};

/**
 * Connect the vendor's shop.
 *
 * A token they create in their own admin rather than an OAuth install: a
 * listed app needs review, a privacy policy and mandatory GDPR webhooks, which
 * is weeks before anyone sees a number. This is the ugly version that works
 * today, and the page is written so swapping in an install button later
 * changes only this form.
 */
export const ShopPage: React.FC = () => {
    const { t } = useTranslation();
    const api = useCommerceApi();
    const { detection, shops, loading, reload } = useShopConnection();

    const live = shops.filter((s) => s.status !== 'revoked');

    return (
        <PageContainer breadcrumbs={[{ label: t('SALES.TITLE') }, { label: t('SALES.SHOP.TITLE') }]}>
            <div className="mx-auto flex max-w-[860px] flex-col gap-7">
                <div>
                    <h1 className="font-heading text-[22px] font-medium tracking-tight">{t('SALES.SHOP.TITLE')}</h1>
                    <p className="mt-1 text-sm text-muted-foreground">{t('SALES.SHOP.SUBTITLE')}</p>
                </div>

                {loading && (
                    <div className="flex justify-center py-16">
                        <Loader2 className="size-5 animate-spin text-muted-foreground" />
                    </div>
                )}

                {/* What the connection is doing, in words, before the controls. */}
                {!loading &&
                    live.map((shop) => (
                        <React.Fragment key={shop.id}>
                            <Reading
                                specs={shopInsights(shop, PLATFORM_LABEL[shop.platform] ?? shop.platform)}
                                title={t('BUSINESS.SALES.SHOP_READING')}
                                description={t('BUSINESS.SALES.SHOP_READING_HINT')}
                                testId="shop-reading"
                            />
                            <ConnectedShop shop={shop} onChanged={reload} />
                        </React.Fragment>
                    ))}

                {!loading && !live.length && (
                    <ConnectForm detection={detection} onConnected={reload} connect={api.connect} />
                )}
            </div>
        </PageContainer>
    );
};

/** A live connection: what it is reading, how far it got, and how to stop it. */
const ConnectedShop: React.FC<{ shop: ShopConnection; onChanged: () => void }> = ({ shop, onChanged }) => {
    const { t } = useTranslation();
    const api = useCommerceApi();
    const [busy, setBusy] = useState<'sync' | 'disconnect' | null>(null);

    const run = async (what: 'sync' | 'disconnect') => {
        setBusy(what);
        try {
            await (what === 'sync' ? api.sync(shop.id) : api.disconnect(shop.id));
            onChanged();
        } finally {
            setBusy(null);
        }
    };

    const broken = shop.status !== 'active';

    return (
        <div className={cn('rounded-xl border bg-card p-5', broken && 'border-destructive/30')}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex size-9 items-center justify-center rounded-lg border">
                        <ShoppingBag className="size-4 text-muted-foreground" />
                    </span>
                    <div>
                        <p className="text-sm font-medium">{shop.shopName || shop.shopDomain}</p>
                        <p className="text-xs text-muted-foreground">
                            {PLATFORM_LABEL[shop.platform] ?? shop.platform} · {shop.shopDomain}
                        </p>
                    </div>
                </div>
                <StatusBadge tone={broken ? 'danger' : 'success'}>
                    {t(broken ? 'SALES.SHOP.STATUS_BROKEN' : 'SALES.SHOP.STATUS_OK')}
                </StatusBadge>
            </div>

            {broken && shop.lastError && (
                <p className="mt-3 flex items-start gap-2 text-sm text-destructive">
                    <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                    {shop.lastError}
                </p>
            )}

            <dl className="mt-4 grid gap-3 sm:grid-cols-4">
                <Fact label={t('SALES.SHOP.READS')} value={shop.grants.map((g) => t(`SALES.GRANT.${g.toUpperCase()}`)).join(', ')} />
                <Fact label={t('SALES.SHOP.ORDERS_IN')} value={formatNumber(shop.backfill.ordersImported ?? 0)} />
                <Fact label={t('SALES.SHOP.PRODUCTS_IN')} value={formatNumber(shop.backfill.productsImported ?? 0)} />
                <div>
                    <dt className="text-xs text-muted-foreground">{t('BUSINESS.SALES.LAST_READ')}</dt>
                    <dd className="mt-0.5 text-sm">
                        <RelativeTime value={shop.lastSyncAt} />
                    </dd>
                </div>
            </dl>

            {/* A backfill of two years does not finish in one request; saying so
                is better than a page that looks finished and keeps growing. */}
            {shop.backfill.status === 'running' && (
                <p className="mt-3 text-xs text-muted-foreground">{t('SALES.SHOP.STILL_READING')}</p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => run('sync')} disabled={busy !== null}>
                    {busy === 'sync' ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <RefreshCw data-icon="inline-start" />}
                    {t('SALES.SHOP.SYNC_NOW')}
                </Button>
                {/* Outlined, never filled: destructive actions are outlined here. */}
                <Button
                    variant="outline"
                    size="sm"
                    className={DESTRUCTIVE_OUTLINE}
                    onClick={() => run('disconnect')}
                    disabled={busy !== null}
                >
                    {t('SALES.SHOP.DISCONNECT')}
                </Button>
            </div>
        </div>
    );
};

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div>
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 text-sm">{value || '—'}</dd>
    </div>
);

const ConnectForm: React.FC<{
    detection: ReturnType<typeof useShopConnection>['detection'];
    connect: ReturnType<typeof useCommerceApi>['connect'];
    onConnected: () => void;
}> = ({ detection, connect, onConnected }) => {
    const { t } = useTranslation();
    const [shopDomain, setShopDomain] = useState('');
    const [token, setToken] = useState('');
    const [grants, setGrants] = useState<CommerceGrant[]>(['catalog', 'sales', 'stock']);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const platform = detection?.platform ?? null;
    const label = platform ? PLATFORM_LABEL[platform] ?? platform : '';

    // A `.myshopify.com` host is a good first guess and almost always right;
    // the vendor can correct it, and it saves them finding it in their admin.
    useEffect(() => {
        if (!shopDomain && detection?.websiteUrl) {
            setShopDomain(detection.websiteUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, ''));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detection?.websiteUrl]);

    if (!detection?.websiteUrl) {
        return <Empty title={t('SALES.SHOP.NO_WEBSITE')} body={t('SALES.SHOP.NO_WEBSITE_BODY')} />;
    }
    if (!platform) {
        return <Empty title={t('SALES.SHOP.NOT_DETECTED')} body={t('SALES.SHOP.NOT_DETECTED_BODY')} />;
    }
    if (!detection.connectable) {
        return <Empty title={t('SALES.SHOP.NOT_SUPPORTED', { platform: label })} body={t('SALES.SHOP.NOT_SUPPORTED_BODY')} />;
    }

    const toggle = (grant: CommerceGrant) =>
        setGrants((current) => (current.includes(grant) ? current.filter((g) => g !== grant) : [...current, grant]));

    const submit = async () => {
        setSaving(true);
        setError(null);
        try {
            await connect({ platform, shopDomain, credentials: { accessToken: token }, grants });
            onConnected();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? t('SALES.SHOP.FAILED'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-5">
            {/* What we found, and how sure we are. `likely` asks rather than
                states — telling someone confidently that they run the platform
                they migrated away from is worse than a question. */}
            <div className="flex items-start gap-3 rounded-xl border bg-card p-5">
                <Link2 className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div>
                    <p className="text-sm">
                        {t(detection.confidence === 'certain' ? 'SALES.SHOP.DETECTED' : 'SALES.SHOP.MAYBE', {
                            platform: label,
                            site: detection.websiteUrl.replace(/^https?:\/\//, ''),
                        })}
                    </p>
                    {detection.evidence.length > 0 && (
                        <p className="mt-1 text-xs text-muted-foreground">
                            {t('SALES.SHOP.EVIDENCE', { evidence: detection.evidence.slice(0, 3).join(', ') })}
                        </p>
                    )}
                </div>
            </div>

            <div className="rounded-xl border bg-card p-5">
                <h2 className="text-sm font-medium">{t('SALES.SHOP.WHAT_WE_READ')}</h2>
                <p className="mt-1 text-xs text-muted-foreground">{t('SALES.SHOP.WHAT_WE_READ_HINT')}</p>

                <div className="mt-4 flex flex-col gap-3">
                    {GRANTS.map(({ key, required }) => (
                        <label key={key} className="flex cursor-pointer items-start gap-3">
                            <Checkbox
                                className="mt-0.5"
                                checked={grants.includes(key)}
                                disabled={required}
                                onCheckedChange={() => toggle(key)}
                            />
                            <span>
                                <span className="block text-sm">{t(`SALES.GRANT.${key.toUpperCase()}`)}</span>
                                <span className="block text-xs text-muted-foreground">
                                    {t(`SALES.GRANT.${key.toUpperCase()}_BODY`)}
                                </span>
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            <div className="rounded-xl border bg-card p-5">
                <h2 className="text-sm font-medium">{t('SALES.SHOP.CREDENTIAL')}</h2>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                    {t(`SALES.SHOP.HOWTO.${platform.toUpperCase()}`, t('SALES.SHOP.HOWTO.DEFAULT'))}
                </p>

                <FieldGroup className="mt-4">
                    <Field>
                        <FieldLabel htmlFor="shop-domain">{t('SALES.SHOP.DOMAIN')}</FieldLabel>
                        <Input
                            id="shop-domain"
                            value={shopDomain}
                            onChange={(e) => setShopDomain(e.target.value)}
                            placeholder="acme.myshopify.com"
                        />
                        <FieldDescription>{t('SALES.SHOP.DOMAIN_HINT')}</FieldDescription>
                    </Field>
                    <Field>
                        <FieldLabel htmlFor="shop-token">{t('SALES.SHOP.TOKEN')}</FieldLabel>
                        <Input
                            id="shop-token"
                            type="password"
                            value={token}
                            onChange={(e) => setToken(e.target.value)}
                            autoComplete="off"
                        />
                        <FieldDescription>{t('SALES.SHOP.TOKEN_HINT')}</FieldDescription>
                    </Field>
                </FieldGroup>

                {error && (
                    <p className="mt-3 flex items-start gap-2 text-sm text-destructive">
                        <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                        {error}
                    </p>
                )}

                <Button variant="outline" className="mt-4" onClick={submit} disabled={saving || !shopDomain.trim() || !token.trim()}>
                    {saving ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <CheckCircle2 data-icon="inline-start" />}
                    {t('SALES.SHOP.CONNECT')}
                </Button>
            </div>
        </div>
    );
};

const Empty: React.FC<{ title: string; body: string }> = ({ title, body }) => (
    <div className="rounded-xl border border-dashed p-8">
        <p className="text-[15px] font-medium">{title}</p>
        <p className="mt-1.5 max-w-[560px] text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
);

export default ShopPage;
